import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Input, SelectField } from '../../containers/Form';
import Strings from '../../Strings';
import { states } from '../../constants';
import { notificationActions } from '../../components/Notification/redux/actions';
import { actions } from '../../pages/SuperUser/Patients/redux/actions';
import {
  RESOLVE_PRIMARY_CARE_PROVIDER_RESULT,
  SEARCH_EXTERNAL_PROVIDERS_RESULT,
} from '../../pages/SuperUser/Patients/redux/constants';
import isValidNpi from '../../utils/npi';
import {
  composeExternalProvider,
  composeManualExternalProvider,
  getDefaultProviderSelections,
  getProviderCompositionOptions,
  getProviderDisplayName,
  getExternalProviderSearchValidation,
  isExternalProviderReady,
} from '../../utils/externalProvider';

const SEARCH_BY_NPI = 'npi';
const SEARCH_BY_NAME = 'name';
const PROVIDER_VIEW = {
  resolving: 'resolving',
  search: 'search',
  results: 'results',
  manual: 'manual',
  summary: 'summary',
};

const providerFormValues = provider => ({
  firstName: provider?.firstName || '',
  middleName: provider?.middleName || '',
  lastName: provider?.lastName || '',
  npi: provider?.npi || '',
  status: provider?.status || '',
  specialtyCode: provider?.specialtyCode || '',
  specialtyName: provider?.specialtyName || '',
  organizationName: provider?.organizationName || '',
  directAddress: provider?.directAddress || '',
  faxNumber: provider?.faxNumber || '',
});

const matchLabel = match => {
  const proposal = match?.proposals?.[0];
  const name = getProviderDisplayName(proposal);
  return [name, proposal?.npi].filter(Boolean).join(' — ');
};

const composeDirectoryProvider = (match, selections, missingChannels) =>
  composeManualExternalProvider({ ...composeExternalProvider(match, selections), ...missingChannels });

function ExternalProviderField(props) {
  const {
    idPrefix,
    label,
    patientId,
    value,
    onChange,
    onDraftChange,
    onAcceptanceChange,
    required,
    autoResolve,
    highlightInvalid,
    validationErrors,
    resolvePrimaryCareProvider,
    searchExternalProviders,
    showNotification,
  } = props;
  const [view, setView] = React.useState(
    value
      ? PROVIDER_VIEW.summary
      : autoResolve && patientId
      ? PROVIDER_VIEW.resolving
      : PROVIDER_VIEW.search,
  );
  const [searchMode, setSearchMode] = React.useState(SEARCH_BY_NPI);
  const [npi, setNpi] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [state, setState] = React.useState('');
  const [matches, setMatches] = React.useState([]);
  const [selectedMatchId, setSelectedMatchId] = React.useState('');
  const [selections, setSelections] = React.useState({});
  const [missingChannels, setMissingChannels] = React.useState({});
  const [manualValues, setManualValues] = React.useState(providerFormValues());
  const [searchReturnView, setSearchReturnView] = React.useState();
  const [manualReturnView, setManualReturnView] = React.useState();
  const [isLoading, setIsLoading] = React.useState(false);
  const [searchMessage, setSearchMessage] = React.useState('');
  const [resultMessage, setResultMessage] = React.useState('');
  const [searchValidationAttempted, setSearchValidationAttempted] = React.useState(false);
  const automaticLookupAttempted = React.useRef(false);

  const selectMatch = React.useCallback(match => {
    setMissingChannels({});
    if (!match) {
      setSelectedMatchId('');
      setSelections({});
      onDraftChange?.(null);
      return;
    }
    const options = getProviderCompositionOptions(match);
    const defaultSelections = getDefaultProviderSelections(options);
    setSelectedMatchId(match.matchId);
    setSelections(defaultSelections);
    onDraftChange?.(composeExternalProvider(match, defaultSelections));
  }, [onDraftChange]);

  React.useEffect(() => {
    if (!autoResolve || !patientId || value || automaticLookupAttempted.current) {
      return;
    }
    automaticLookupAttempted.current = true;
    setView(PROVIDER_VIEW.resolving);
    setIsLoading(true);
    resolvePrimaryCareProvider(patientId)
      .then(response => {
        if (response.type !== RESOLVE_PRIMARY_CARE_PROVIDER_RESULT) {
          setSearchMessage(Strings.externalProviderDirectory.resolveFailed);
          setView(PROVIDER_VIEW.search);
          return;
        }
        if (response.response?.result === 'found' && response.response.match) {
          setMatches([response.response.match]);
          selectMatch(response.response.match);
          setResultMessage(Strings.externalProviderDirectory.resolvedFromAthena);
          setView(PROVIDER_VIEW.results);
        } else {
          const reason = response.response?.reason;
          const reasonMessages = {
            athena_disabled: Strings.externalProviderDirectory.athenaDisabled,
            patient_not_linked_to_athena: Strings.externalProviderDirectory.athenaMappingMissing,
            pcp_not_set_in_athena: Strings.externalProviderDirectory.pcpNotFound,
          };
          setSearchMessage(reasonMessages[reason] || Strings.externalProviderDirectory.pcpNotFound);
          setView(PROVIDER_VIEW.search);
        }
      })
      .catch(() => {
        setSearchMessage(Strings.externalProviderDirectory.resolveFailed);
        setView(PROVIDER_VIEW.search);
      })
      .finally(() => setIsLoading(false));
  }, [autoResolve, patientId, resolvePrimaryCareProvider, selectMatch, value]);

  const selectedMatch = matches.find(match => match.matchId === selectedMatchId);
  const compositionOptions = getProviderCompositionOptions(selectedMatch);
  const directoryDraft = selectedMatch ? composeDirectoryProvider(selectedMatch, selections, missingChannels) : null;
  const manualDraft = composeManualExternalProvider(manualValues);
  const providerValidation = validationErrors?.find(error => error.property === idPrefix);
  const providerErrors = providerValidation?.errors || [];
  const searchValidation = getExternalProviderSearchValidation({ searchMode, npi, firstName, lastName });
  const searchFieldErrors = field => {
    if (!searchValidationAttempted || !searchValidation[field]) {
      return [];
    }
    return [searchValidation[field] === 'invalid' ? Strings.invalidNpi : Strings.fieldIsRequired];
  };

  const returnToView = targetView => {
    const draft = targetView === PROVIDER_VIEW.summary
      ? value
      : targetView === PROVIDER_VIEW.results
      ? directoryDraft
      : null;
    onDraftChange?.(draft);
    onAcceptanceChange?.(targetView === PROVIDER_VIEW.summary);
    setView(targetView);
  };

  const canSearch = !Object.values(searchValidation).some(Boolean);

  const search = () => {
    setSearchValidationAttempted(true);
    if (!canSearch || !patientId) {
      return;
    }
    const criteria =
      searchMode === SEARCH_BY_NPI
        ? { npi: npi.trim() }
        : {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            state: state || undefined,
            limit: 20,
          };
    setIsLoading(true);
    setSearchMessage('');
    searchExternalProviders(patientId, criteria)
      .then(response => {
        if (response.type !== SEARCH_EXTERNAL_PROVIDERS_RESULT) {
          const message =
            response?.response?.data?.error?.message ||
            response?.response?.data?.error_msg ||
            Strings.externalProviderDirectory.searchFailed;
          showNotification(message, 5000, true);
          return;
        }
        const foundMatches = response.response?.matches || [];
        if (foundMatches.length > 0) {
          setMatches(foundMatches);
          selectMatch(foundMatches.length === 1 ? foundMatches[0] : null);
          setResultMessage('');
          setSearchReturnView(undefined);
          setView(PROVIDER_VIEW.results);
        } else {
          setSearchMessage(Strings.externalProviderDirectory.noResults);
        }
        if (response.response?.warnings?.length > 0) {
          showNotification(Strings.externalProviderDirectory.partialResults, 5000, true);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const openSearch = () => {
    setSearchReturnView(view);
    setSearchMessage('');
    setNpi('');
    setFirstName('');
    setLastName('');
    setState('');
    onDraftChange?.(null);
    onAcceptanceChange?.(false);
    setSearchValidationAttempted(false);
    setView(PROVIDER_VIEW.search);
  };

  const openManualEntry = () => {
    const provider = view === PROVIDER_VIEW.summary
      ? value
      : view === PROVIDER_VIEW.results
      ? directoryDraft
      : undefined;
    const values = providerFormValues(provider);
    setManualValues(values);
    onDraftChange?.(composeManualExternalProvider(values));
    onAcceptanceChange?.(false);
    setManualReturnView(view);
    setView(PROVIDER_VIEW.manual);
  };

  const useProvider = provider => {
    onChange(provider);
    onDraftChange?.(provider);
    onAcceptanceChange?.(true);
    setView(PROVIDER_VIEW.summary);
  };

  const setManualValue = (field, fieldValue) => {
    const values = { ...manualValues, [field]: fieldValue };
    setManualValues(values);
    onDraftChange?.(composeManualExternalProvider(values));
  };

  const setSelection = (field, option) => {
    const nextSelections = { ...selections, [field]: option?.value || '' };
    setSelections(nextSelections);
    onDraftChange?.(selectedMatch ? composeDirectoryProvider(selectedMatch, nextSelections, missingChannels) : null);
  };

  const optionField = (field, fieldLabel, optional = false, isRequired = !optional) => {
    const fieldOptions = compositionOptions[field] || [];
    if (fieldOptions.length === 0 && optional) {
      return null;
    }
    const data = optional ? [{ value: '', label: Strings.none }].concat(fieldOptions) : fieldOptions;
    return (
      <SelectField
        name={`${idPrefix}-${field}`}
        id={`${idPrefix}-${field}`}
        label={fieldLabel}
        value={selections[field] || ''}
        onChange={option => setSelection(field, option)}
        data={data}
        placeholder={Strings.noOptionSelected}
        isRequired={isRequired}
      />
    );
  };

  const channelField = (field, fieldLabel, otherField) => {
    const isRequired = !directoryDraft?.[otherField]?.trim();
    if (compositionOptions[field]?.length > 0) {
      return optionField(field, fieldLabel, true, isRequired);
    }
    return (
      <Input
        name={`${idPrefix}-${field}`}
        id={`${idPrefix}-${field}`}
        label={fieldLabel}
        value={missingChannels[field] || ''}
        onChange={event => {
          const nextChannels = { ...missingChannels, [field]: event.target.value };
          setMissingChannels(nextChannels);
          onDraftChange?.(composeDirectoryProvider(selectedMatch, selections, nextChannels));
        }}
        isRequired={isRequired}
        highlightInvalid={highlightInvalid}
      />
    );
  };

  const manualInput = (field, fieldLabel, isRequired = false) => (
    <Input
      name={`${idPrefix}-manual-${field}`}
      id={`${idPrefix}-manual-${field}`}
      label={fieldLabel}
      value={manualValues[field]}
      onChange={event => setManualValue(field, event.target.value)}
      isRequired={isRequired}
      highlightInvalid={highlightInvalid}
      errorsForTooltip={
        field === 'npi' && manualValues.npi && !isValidNpi(manualValues.npi.trim())
          ? [Strings.invalidNpi]
          : []
      }
    />
  );

  const selectedSummary = value ? (
    <div className="external-provider-summary">
      <div>
        <strong>{getProviderDisplayName(value)}</strong>
        {value.npi && (
          <span>{`${Strings.externalProviderDirectory.npi}: ${value.npi}`}</span>
        )}
        {value.status && <span>{`${Strings.status}: ${value.status}`}</span>}
        {(value.specialtyName || value.specialtyCode) && (
          <span>
            {[value.specialtyName, value.specialtyCode].filter(Boolean).join(' — ')}
          </span>
        )}
        {value.organizationName && <span>{value.organizationName}</span>}
        {value.directAddress && (
          <span>{`${Strings.externalProviderDirectory.directAddress}: ${value.directAddress}`}</span>
        )}
        {value.faxNumber && (
          <span>{`${Strings.externalProviderDirectory.faxNumber}: ${value.faxNumber}`}</span>
        )}
      </div>
      <div className="external-provider-actions">
        <button type="button" className="link-button" onClick={openSearch}>
          {Strings.externalProviderDirectory.searchAnother}
        </button>
        <button type="button" className="link-button" onClick={openManualEntry}>
          {Strings.externalProviderDirectory.editManually}
        </button>
        {!required && (
          <button
            type="button"
            className="link-button"
            onClick={() => {
              onChange(undefined);
              setView(PROVIDER_VIEW.search);
            }}
          >
            {Strings.externalProviderDirectory.removeProvider}
          </button>
        )}
      </div>
    </div>
  ) : null;

  return (
    <section
      className={`external-provider-field grid-full-width ${highlightInvalid && !value ? 'invalid-control-background' : ''}`}
    >
      <h4 className={required ? 'required-asterisk' : ''}>{label}</h4>
      {providerErrors.map(error => (
        <div
          className={providerValidation.informational ? 'external-provider-message' : 'external-provider-error'}
          key={error}
        >
          {error}
        </div>
      ))}
      {view === PROVIDER_VIEW.resolving && (
        <div className="external-provider-message">{Strings.loading}</div>
      )}
      {view === PROVIDER_VIEW.summary && selectedSummary}
      {view === PROVIDER_VIEW.search && (
        <div className="external-provider-search">
          <SelectField
            name={`${idPrefix}-search-mode`}
            id={`${idPrefix}-search-mode`}
            label={Strings.externalProviderDirectory.searchBy}
            value={searchMode}
            onChange={option => {
              setSearchMode(option.value);
              setSearchValidationAttempted(false);
            }}
            data={[
              { value: SEARCH_BY_NPI, label: Strings.externalProviderDirectory.npi },
              { value: SEARCH_BY_NAME, label: Strings.externalProviderDirectory.name },
            ]}
          />
          {searchMode === SEARCH_BY_NPI ? (
            <Input
              name={`${idPrefix}-search-npi`}
              id={`${idPrefix}-search-npi`}
              label={Strings.externalProviderDirectory.npi}
              value={npi}
              onChange={event => setNpi(event.target.value)}
              isRequired
              highlightInvalid={searchValidationAttempted}
              errors={searchFieldErrors('npi')}
              errorsForTooltip={
                searchValidation.npi === 'invalid' ? [Strings.invalidNpi] : []
              }
            />
          ) : (
            <>
              <Input
                name={`${idPrefix}-search-first-name`}
                id={`${idPrefix}-search-first-name`}
                label={Strings.firstName}
                value={firstName}
                onChange={event => setFirstName(event.target.value)}
                isRequired
                highlightInvalid={searchValidationAttempted}
                errors={searchFieldErrors('firstName')}
              />
              <Input
                name={`${idPrefix}-search-last-name`}
                id={`${idPrefix}-search-last-name`}
                label={Strings.lastName}
                value={lastName}
                onChange={event => setLastName(event.target.value)}
                isRequired
                highlightInvalid={searchValidationAttempted}
                errors={searchFieldErrors('lastName')}
              />
              <SelectField
                name={`${idPrefix}-search-state`}
                id={`${idPrefix}-search-state`}
                label={Strings.state}
                value={state}
                onChange={option => setState(option?.value || '')}
                data={[{ value: '', label: Strings.all }].concat(
                  states.map(item => ({
                    value: item.abbreviation,
                    label: `${item.name} (${item.abbreviation})`,
                  })),
                )}
                isSearchable
              />
            </>
          )}
          <div className="external-provider-search-actions">
            <button
              type="button"
              className="brand-blue"
              disabled={!canSearch || !patientId || isLoading}
              onClick={search}
            >
              {isLoading ? Strings.loading : Strings.search}
            </button>
            <button type="button" className="link-button" onClick={openManualEntry}>
              {Strings.externalProviderDirectory.enterManually}
            </button>
            {searchReturnView && (
              <button type="button" className="link-button" onClick={() => returnToView(searchReturnView)}>
                {Strings.cancel}
              </button>
            )}
          </div>
          {searchMessage && <div className="external-provider-message">{searchMessage}</div>}
        </div>
      )}
      {view === PROVIDER_VIEW.results && (
        <div className="external-provider-results">
          {resultMessage && <div className="external-provider-message">{resultMessage}</div>}
          {matches.length > 1 && (
            <SelectField
              name={`${idPrefix}-match`}
              id={`${idPrefix}-match`}
              label={Strings.externalProviderDirectory.match}
              value={selectedMatchId}
              onChange={option => selectMatch(matches.find(match => match.matchId === option.value))}
              data={matches.map(match => ({ value: match.matchId, label: matchLabel(match) }))}
              placeholder={Strings.noOptionSelected}
              isRequired
            />
          )}
          {selectedMatch && (
            <div className="external-provider-composition">
              {optionField('name', Strings.externalProviderDirectory.name)}
              {optionField('npi', Strings.externalProviderDirectory.npi)}
              {optionField('status', Strings.status, true)}
              {optionField('specialty', Strings.externalProviderDirectory.specialty, true)}
              {optionField('organization', Strings.organization, true)}
              <div className="external-provider-channels">
                {channelField('directAddress', Strings.externalProviderDirectory.directAddress, 'faxNumber')}
                {channelField('faxNumber', Strings.externalProviderDirectory.faxNumber, 'directAddress')}
              </div>
              <div className="external-provider-message">
                {Strings.externalProviderDirectory.communicationChannelRequired}
              </div>
            </div>
          )}
          <div className="external-provider-result-actions">
            {selectedMatch && (
              <button
                type="button"
                className="brand-blue"
                disabled={!isExternalProviderReady(directoryDraft)}
                onClick={() => useProvider(directoryDraft)}
              >
                {Strings.externalProviderDirectory.useProvider}
              </button>
            )}
            <button type="button" className="link-button" onClick={openSearch}>
              {Strings.externalProviderDirectory.searchAnother}
            </button>
            <button type="button" className="link-button" onClick={openManualEntry}>
              {Strings.externalProviderDirectory.enterManually}
            </button>
            {value && (
              <button
                type="button"
                className="link-button"
                onClick={() => returnToView(PROVIDER_VIEW.summary)}
              >
                {Strings.cancel}
              </button>
            )}
          </div>
          {directoryDraft && !isExternalProviderReady(directoryDraft) && (
            <div className="external-provider-message">
              {Strings.externalProviderDirectory.requiredProviderData}
            </div>
          )}
        </div>
      )}
      {view === PROVIDER_VIEW.manual && (
        <div className="external-provider-manual">
          <div className="external-provider-message">
            {Strings.externalProviderDirectory.manualEntryHelp}
          </div>
          {manualInput('firstName', Strings.firstName, true)}
          {manualInput('middleName', Strings.externalProviderDirectory.middleName)}
          {manualInput('lastName', Strings.lastName, true)}
          {manualInput('npi', Strings.externalProviderDirectory.npi, true)}
          <SelectField
            name={`${idPrefix}-manual-status`}
            id={`${idPrefix}-manual-status`}
            label={Strings.status}
            value={manualValues.status}
            onChange={option => setManualValue('status', option?.value || '')}
            data={[
              { value: '', label: Strings.none },
              { value: 'active', label: Strings.externalProviderDirectory.active },
              { value: 'deactivated', label: Strings.externalProviderDirectory.deactivated },
            ]}
          />
          {manualInput('organizationName', Strings.organization)}
          {manualInput('specialtyName', Strings.externalProviderDirectory.specialty)}
          {manualInput('specialtyCode', Strings.externalProviderDirectory.specialtyCode)}
          {manualInput('directAddress', Strings.externalProviderDirectory.directAddress, !manualValues.faxNumber)}
          {manualInput('faxNumber', Strings.externalProviderDirectory.faxNumber, !manualValues.directAddress)}
          <div className="external-provider-manual-actions">
            <button
              type="button"
              className="brand-blue"
              disabled={!isExternalProviderReady(manualDraft)}
              onClick={() => useProvider(manualDraft)}
            >
              {Strings.externalProviderDirectory.useEnteredProvider}
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => returnToView(
                manualReturnView || (value ? PROVIDER_VIEW.summary : PROVIDER_VIEW.search)
              )}
            >
              {Strings.cancel}
            </button>
          </div>
          {!isExternalProviderReady(manualDraft) && (
            <div className="external-provider-message">
              {Strings.externalProviderDirectory.requiredProviderData}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

ExternalProviderField.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  patientId: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onDraftChange: PropTypes.func,
  onAcceptanceChange: PropTypes.func,
  required: PropTypes.bool,
  autoResolve: PropTypes.bool,
  highlightInvalid: PropTypes.bool,
  validationErrors: PropTypes.array,
  resolvePrimaryCareProvider: PropTypes.func.isRequired,
  searchExternalProviders: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  resolvePrimaryCareProvider: patientId => dispatch(actions.resolvePrimaryCareProvider(patientId)),
  searchExternalProviders: (patientId, criteria) => dispatch(actions.searchExternalProviders(patientId, criteria)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(null, mapDispatchToProps)(ExternalProviderField);
